'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import Container from '@/components/ui/container';
import { cn } from '@/lib/utils';
import { getDirectusAssetURL } from '@/lib/directus/directus-utils';
import { BlockFeaturedLinks, FeaturedLinksItem, Page, Post, DirectusFile } from '@/types/directus-schema';

interface FeaturedLinksProps {
	data: BlockFeaturedLinks;
	itemId?: string;
}

/**
 * Resolves the image URL from a FeaturedLinksItem.
 * The image can be a string (asset ID) or a DirectusFile object.
 */
function getItemImageUrl(image: string | DirectusFile | null | undefined): string {
	if (!image) return '';
	if (typeof image === 'string') return getDirectusAssetURL(image);

	return getDirectusAssetURL(image.id);
}

/**
 * Resolves the URL for a featured link item based on its referenced page or post.
 * The item field comes from an M2A junction: item: [{ collection, item: { permalink/slug } }]
 */
function getItemUrl(item: FeaturedLinksItem): string {
	const refs = item.item;
	if (!refs) return '#';

	// M2A junction array format: [{ collection: "posts", item: { slug: "..." } }]
	if (Array.isArray(refs) && refs.length > 0) {
		const firstRef = refs[0] as any;
		const refItem = firstRef?.item;
		if (!refItem) return '#';

		if (firstRef.collection === 'pages' && refItem.permalink) {
			return refItem.permalink;
		}

		if (firstRef.collection === 'posts' && refItem.slug) {
			return `/advies/${refItem.slug}`;
		}

		return '#';
	}

	// Direct reference format (fallback)
	if (typeof refs === 'object' && !Array.isArray(refs)) {
		const ref = refs as any;
		if ('permalink' in ref && ref.permalink) return ref.permalink;
		if ('slug' in ref && ref.slug) return `/advies/${ref.slug}`;
	}

	return '#';
}

const FeaturedLinks = ({ data, itemId }: FeaturedLinksProps) => {
	const { items: rawItems = [] } = data;
	const [activeIndex, setActiveIndex] = useState(0);

	// Unwrap M2M junction table: items come as { block_featuredlinks_item_id: { ... } }
	const validItems: FeaturedLinksItem[] = (rawItems || [])
		.map((junctionItem: any) => {
			// If junction wrapper exists, unwrap it
			const item = junctionItem?.block_featuredlinks_item_id || junctionItem;

			return item;
		})
		.filter((item: any) => item && item.title);

	if (validItems.length === 0) return null;

	// Collect all image URLs for preloading
	const imageUrls = validItems.map((item) => getItemImageUrl(item.image));

	return (
		<section className="py-16 md:py-24" data-directus-id={itemId}>
			<Container>
				<div className="flex flex-col md:grid md:grid-cols-3 gap-8 md:gap-12">
					{/* Left column: Image preview (hidden on mobile) */}
					<div className="hidden md:block md:col-span-1">
						<div className="">
							<div className="relative aspect-square w-full overflow-hidden rounded-lg bg-gray-100">
								{/* Render all images stacked, toggle opacity for crossfade */}
								{validItems.map((item, index) => {
									const url = imageUrls[index];
									if (!url) return null;

									return (
										<Image
											key={item.id}
											src={url}
											alt={item.title}
											fill
											sizes="(max-width: 768px) 0px, 500px"
											className={cn(
												'object-cover transition-opacity duration-500 ease-in-out',
												index === activeIndex ? 'opacity-100' : 'opacity-0',
											)}
											priority={index === 0}
										/>
									);
								})}
							</div>
						</div>
					</div>

					{/* Right column: Item list */}
					<div className="md:col-span-2">
						<div className="flex flex-col">
							{validItems.map((item, index) => {
								const url = getItemUrl(item);
								const isActive = index === activeIndex;
								const isLast = index === validItems.length - 1;

								return (
									<Link
										key={item.id}
										href={url}
										className={cn(
											'group flex items-center justify-between gap-4 py-6 md:py-8 transition-colors duration-200',
											!isLast && 'border-b border-[#446084]/15',
											'hover:bg-[#F9FAFB] -mx-4 px-4 md:-mx-6 md:px-6 rounded-lg',
										)}
										onMouseEnter={() => setActiveIndex(index)}
									>
										<div className="flex-1 min-w-0">
											<h3
												className="text-xl md:text-2xl font-heading font-medium text-[#446084]"
											>
												{item.title}
											</h3>
											{item.description && (
												<p className="mt-2 text-[#000000] line-clamp-2">
													{item.description}
												</p>
											)}
										</div>
										<div
											className={cn(
												'flex-shrink-0 transition-all duration-200',
												isActive
													? 'text-orange translate-x-0 opacity-100'
													: 'text-[#446084]/40 -translate-x-1 opacity-0 md:opacity-100',
												'group-hover:text-orange group-hover:translate-x-0 group-hover:opacity-100',
											)}
										>
											<ArrowRight className="size-5 md:size-6 transition-transform duration-200 group-hover:translate-x-1" />
										</div>
									</Link>
								);
							})}
						</div>
					</div>
				</div>

				{/* Hidden preload images for fast switching */}
				<div className="hidden" aria-hidden="true">
					{imageUrls.map(
						(url, i) =>
							url && (
								<Image
									key={`preload-${i}`}
									src={url}
									alt=""
									width={1}
									height={1}
									priority
								/>
							),
					)}
				</div>
			</Container>
		</section>
	);
};

export default FeaturedLinks;
